import { InteractionStatus } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { useEffect, useState, type ReactNode } from "react";
import { acquireIdToken, startLogin } from "./msal";

function LoginScreen({ onSignIn, busy, error }: { onSignIn: () => Promise<void>; busy: boolean; error?: string }) {
  return (
    <main className="admin-login-shell">
      <section className="admin-login-card">
        <p className="eyebrow">aviOS Pricing Master</p>
        <h1>Sign in required</h1>
        <p className="muted">Use your Microsoft work or school account to access authenticated Pricing Master tools.</p>
        {error ? <p className="error">{error}</p> : null}
        <button type="button" onClick={() => void onSignIn()} disabled={busy}>
          {busy ? "Opening Microsoft…" : "Sign in with Microsoft"}
        </button>
      </section>
    </main>
  );
}

function loginErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return "Microsoft sign-in failed. Try again.";
}

export function AuthGate({ children }: { children: ReactNode }) {
  const { instance, inProgress, accounts } = useMsal();
  const [checked, setChecked] = useState(false);
  const [tokenOk, setTokenOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (inProgress === InteractionStatus.Startup || inProgress === InteractionStatus.HandleRedirect) {
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        await acquireIdToken(instance);
        if (!cancelled) {
          setTokenOk(true);
          setError(undefined);
        }
      } catch {
        if (!cancelled) {
          setTokenOk(false);
        }
      } finally {
        if (!cancelled) {
          setChecked(true);
          setBusy(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [accounts, inProgress, instance]);

  const onSignIn = async () => {
    if (busy) {
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      await startLogin(instance);
    } catch (cause) {
      setBusy(false);
      setError(loginErrorMessage(cause));
    }
  };

  if (!checked || inProgress === InteractionStatus.Startup || inProgress === InteractionStatus.HandleRedirect) {
    return (
      <main className="admin-login-shell">
        <section className="admin-login-card">
          <h1>Finishing sign-in…</h1>
        </section>
      </main>
    );
  }

  if (!tokenOk) {
    return <LoginScreen onSignIn={onSignIn} busy={busy} error={error} />;
  }

  return <>{children}</>;
}
